import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  Select,
  SelectProps,
  FormHelperText,
} from '@mui/material';

type SelectInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  required?: boolean;
  children: React.ReactNode;
} & Omit<SelectProps, 'name' | 'value' | 'onBlur' | 'ref' | 'error'>;

export function SelectInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  required = false,
  children,
  ...rest
}: SelectInputProps<TFieldValues, TName>) {
  const { error } = form.getFieldState(name, form.formState);

  const { onChange, ...field } = form.register(name);

  return (
    <FormControl fullWidth required={required} error={!!error}>
      <InputLabel>{label}</InputLabel>
      <Select
        {...rest}
        {...field}
        label={label}
        value={form.watch(name) || ''}
        onChange={(event, child) => {
          onChange(event);
          rest.onChange?.(event, child);
        }}
      >
        {children}
      </Select>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
}
